/**
 * components/forms/SmartTableImportModal.tsx
 *
 * Modal for importing a CSV, TSV, or Excel file into the SmartTable.
 * Shows a drag-and-drop zone, a format guide (when no file loaded), and
 * a preview of the first 5 parsed rows once a file is selected.
 *
 * Created April 2026: part of the smart-table form creation workflow.
 * Updated April 2026: added inline example table covering all field types
 *   and the Condition column; updated preview to show Options and Condition.
 * Updated April 2026: added Intitulé (name) and Question columns in the
 *   example, preview, and documentation, mirroring the catalog structure.
 * Updated April 2026: catch block now logs parse errors via console.error.
 * Updated April 2026: captureError payload redacted to send only fileExtension and fileSize
 *   instead of the raw filename to avoid leaking client data to Sentry.
 * Updated April 2026: hardened fileExtension extraction - only extracts the portion after the
 *   last dot when a dot is present, normalised to lowercase and capped at 10 chars; falls back
 *   to 'unknown' so the full filename is never sent to Sentry.
 * Updated April 2026: tightened fileExtension guard - dot must be at position > 0 (excludes
 *   dot-files like .bashrc) and the extension part must be non-empty (excludes trailing dots
 *   like report.); both edge cases now fall back to 'unknown'.
 * Updated April 2026: extracted format guide into SmartTableFormatGuide;
 *   added Obligatoire column to preview table.
 * Updated April 2026: moved FIELD_TYPE_OPTIONS import to shared constants module.
 */

import { useState, useRef, useCallback } from 'react';
import { Upload, FileSpreadsheet, X, AlertCircle } from 'lucide-react';
import { Modal } from '../ui/Modal';
import Button from '../ui/Button';
import { parseImportFile, ACCEPTED_IMPORT_EXTENSIONS } from '../../utils/smartTableParser';
import type { SmartTableRow } from '../../utils/smartTableParser';
import { FIELD_TYPE_OPTIONS } from '../../constants/fieldTypes';
import { SmartTableFormatGuide } from './SmartTableFormatGuide';
import { captureError } from '../../utils/errorReporting';

interface SmartTableImportModalProps {
  onImport: (rows: SmartTableRow[]) => void;
  onClose: () => void;
}

export function SmartTableImportModal({ onImport, onClose }: SmartTableImportModalProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [previewRows, setPreviewRows] = useState<SmartTableRow[]>([]);
  const [allRows, setAllRows] = useState<SmartTableRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(async (f: File) => {
    setFile(f);
    setError(null);
    setParsing(true);
    try {
      const rows = await parseImportFile(f);
      if (rows.length === 0) {
        setError('Aucune ligne valide trouvée dans le fichier. Vérifiez que la première ligne contient des en-têtes.');
        setAllRows([]);
        setPreviewRows([]);
      } else {
        setAllRows(rows);
        setPreviewRows(rows.slice(0, 5));
      }
    } catch (err) {
      console.error('SmartTable import parse error:', err);
      const dotIndex = f.name.lastIndexOf('.');
      const fileExtension = (dotIndex > 0 && f.name.length > dotIndex + 1) ? f.name.slice(dotIndex + 1).toLowerCase().slice(0, 10) : 'unknown';
      captureError(err, { component: 'SmartTableImportModal', action: 'parseImportFile', fileExtension, fileSize: f.size });
      setError('Impossible de lire le fichier. Vérifiez que le format est valide (CSV, TSV, XLSX ou XLS).');
      setAllRows([]);
      setPreviewRows([]);
    } finally {
      setParsing(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const dropped = e.dataTransfer.files[0];
      if (dropped) handleFile(dropped);
    },
    [handleFile]
  );

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) handleFile(selected);
  };

  const handleImport = () => {
    if (allRows.length > 0) { onImport(allRows); onClose(); }
  };

  const getTypeLabel = (value: string) =>
    FIELD_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value;

  const hasOptions = previewRows.some((r) => r.options.length > 0);
  const hasCondition = previewRows.some((r) => r.conditionRaw);
  const hasOptional = previewRows.some((r) => !r.isRequired);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Importer un tableau"
      size="lg"
      footer={
        <div className="flex justify-between items-center">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={handleImport} disabled={allRows.length === 0 || parsing}>
            <Upload className="w-4 h-4 mr-2" />
            Importer {allRows.length > 0 ? `(${allRows.length} élément${allRows.length > 1 ? 's' : ''})` : ''}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 py-1">
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all cursor-pointer ${
            isDragging
              ? 'border-primary-400 bg-primary-50'
              : file
              ? 'border-success-400 bg-success-50'
              : 'border-neutral-200 bg-neutral-50 hover:border-neutral-300 hover:bg-white'
          }`}
          onClick={() => inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_IMPORT_EXTENSIONS}
            className="hidden"
            onChange={handleInputChange}
          />
          {file ? (
            <div className="flex flex-col items-center gap-2">
              <FileSpreadsheet className="w-10 h-10 text-success-500" />
              <p className="font-medium text-neutral-900 text-sm">{file.name}</p>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setFile(null);
                  setAllRows([]);
                  setPreviewRows([]);
                  setError(null);
                  if (inputRef.current) inputRef.current.value = '';
                }}
                className="flex items-center gap-1 text-xs text-neutral-400 hover:text-neutral-600 transition-colors"
              >
                <X className="w-3 h-3" />
                Changer de fichier
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload className="w-10 h-10 text-neutral-300" />
              <p className="font-medium text-neutral-700 text-sm">
                Déposez votre fichier ici ou cliquez pour parcourir
              </p>
              <p className="text-xs text-neutral-400">CSV, TSV, XLSX, XLS</p>
            </div>
          )}
        </div>

        {parsing && (
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            Analyse du fichier...
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 p-3 bg-error-50 border border-error-200 rounded-xl text-sm text-error-700">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {previewRows.length > 0 ? (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-neutral-700">
                Aperçu - {allRows.length} élément{allRows.length > 1 ? 's' : ''} détecté{allRows.length > 1 ? 's' : ''}
              </p>
              {allRows.length > 5 && (
                <span className="text-xs text-neutral-400">Affichage des 5 premiers</span>
              )}
            </div>
            <div className="overflow-x-auto rounded-xl border border-neutral-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-neutral-50 border-b border-neutral-200">
                    <th className="text-left px-3 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide">Intitulé</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide hidden sm:table-cell">Question client</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide w-32">Type</th>
                    {hasOptions && (
                      <th className="text-left px-3 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide hidden sm:table-cell">Options</th>
                    )}
                    {hasCondition && (
                      <th className="text-left px-3 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide hidden sm:table-cell">Condition</th>
                    )}
                    {hasOptional && (
                      <th className="text-left px-3 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide">Requis</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {previewRows.map((row, i) => (
                    <tr key={i} className="bg-white">
                      <td className="px-3 py-2 text-neutral-900 font-medium truncate max-w-[140px]">{row.name || row.label}</td>
                      <td className="px-3 py-2 text-neutral-500 text-xs truncate max-w-[180px] hidden sm:table-cell">
                        {row.label || <span className="text-neutral-300 italic">- (utilise l'intitulé)</span>}
                      </td>
                      <td className="px-3 py-2">
                        <span className="inline-block px-2 py-0.5 bg-neutral-100 rounded text-xs text-neutral-600">
                          {getTypeLabel(row.type)}
                        </span>
                      </td>
                      {hasOptions && (
                        <td className="px-3 py-2 text-neutral-500 text-xs truncate max-w-[160px] hidden sm:table-cell">
                          {row.options.length > 0 ? row.options.join(', ') : '-'}
                        </td>
                      )}
                      {hasCondition && (
                        <td className="px-3 py-2 text-neutral-500 text-xs truncate max-w-[160px] hidden sm:table-cell">
                          {row.conditionRaw || '-'}
                        </td>
                      )}
                      {hasOptional && (
                        <td className="px-3 py-2 text-xs">
                          {row.isRequired
                            ? <span className="text-neutral-400">Oui</span>
                            : <span className="text-neutral-400 italic">Non</span>
                          }
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <SmartTableFormatGuide />
        )}
      </div>
    </Modal>
  );
}
